import React from 'react';
import {
    RouterProvider,
    createHashRouter,
    Navigate,
    useParams
} from 'react-router-dom';
import LogInForm from './components/Login/LogInForm';
import WrapperComponent from './components/Wrapper/WrapperComponent';
import MembershipFormPage from './components/MembershipForm/MembershipFormPage';
import ErrorScreen from './components/ErrorResponseScreen/ErrorScreen';
import SuccessScreen from './components/SuccessResponse/SuccessScreen';
import ToastContainerWrapper from './generic-components/statusProp/StatusPopUp';
import { AuthProvider, useAuth } from './context/AuthContext'; // Import AuthProvider and useAuth
import MatchMakingForm from './pages/MatchMakingForm/MatchMakingForm';
import DashboardLayout from './components/DashboardLayout/DashboardLayout';
import Home from './pages/Home/Home';
import Profile from './pages/Profile/Profile';
import MatchMakingAuthForm from './pages/MatchMakingAuthForm/MatchMakingAuthForm';
import ApplicationList from './pages/Application/ApplicationList';
import ApplicationDetails from './pages/Application/ApplicationDetails';
import CreateRequest from './pages/Create Request/CreateRequest';
import MatchMakingPartnerDetails from './pages/MatchMakingPartnerDetails/MatchMakingPartnerDetails';
import { ShortForm } from './pages/ShortForm';
import ServicesPage from './components/ServicesPage';
import {
    CategoryService,
    PartnerCompanies,
    PartnerOffers,
    SubCategories
} from './components/CategoryServices';


interface AppParams {
    route: string | null;
}

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
    children
}) => {
    const {
        taxonomyCategoryIds,
        taxonomySubCategoryIds,
        partnerId,
        parterToOfferId,
        categoryId
    } = useParams<{
        taxonomyCategoryIds: string;
        taxonomySubCategoryIds: string;
        partnerId: string;
        parterToOfferId: string;
        categoryId: string;
    }>();
    const { isAuthenticated } = useAuth();
    if (!isAuthenticated) {
        return (
            <Navigate
                to={`/categories/${categoryId}/${taxonomyCategoryIds}/${taxonomySubCategoryIds}/${partnerId}/login/${parterToOfferId}`}
                replace
            />
        ); // Corrected template literal
    }
    return <>{children}</>;
};

const App: React.FC<AppParams> = ({ route }) => {
    const customerRouter = createHashRouter([
        {
            element: <ServicesPage />,
            path: '/',
            children: [
                {
                    path: 'categories/:categoryId',
                    element: <CategoryService />
                },
                {
                    path: 'categories/:categoryId/:taxonomyCategoryIds',
                    element: <SubCategories />
                },
                {
                    path: 'categories/:categoryId/:taxonomyCategoryIds/:taxonomySubCategoryIds',
                    element: <PartnerCompanies />
                },
                {
                    path: 'categories/:categoryId/:taxonomyCategoryIds/:taxonomySubCategoryIds/:partnerId',
                    element: <PartnerOffers />
                }
            ]
        },
        {
            element: (
                <WrapperComponent hidePartnershipSection={true} forceLTR={true}>
                    <LogInForm />
                </WrapperComponent>
            ),
            path: '/categories/:categoryId/:taxonomyCategoryIds/:taxonomySubCategoryIds/:partnerId/login/:parterToOfferId'
        },
        {
            element: (
                <ProtectedRoute>
                    <WrapperComponent hidePartnershipSection={true}>
                        <MembershipFormPage />
                    </WrapperComponent>
                </ProtectedRoute>
            ),
            path: '/categories/:categoryId/:taxonomyCategoryIds/:taxonomySubCategoryIds/:partnerId/membership-form/:parterToOfferId'
        },
        {
            element: (
                <ProtectedRoute>
                    <WrapperComponent hidePartnershipSection={true}>
                        <SuccessScreen />
                    </WrapperComponent>
                </ProtectedRoute>
            ),
            path: '/categories/:categoryId/:taxonomyCategoryIds/:taxonomySubCategoryIds/:partnerId/success-page/:parterToOfferId'
        },
        {
            element: (
                <ProtectedRoute>
                    <WrapperComponent hidePartnershipSection={true}>
                        <ErrorScreen />
                    </WrapperComponent>
                </ProtectedRoute>
            ),
            path: '/categories/:categoryId/:taxonomyCategoryIds/:taxonomySubCategoryIds/:partnerId/error-page/:parterToOfferId'
        },
        {
            element: (
                <WrapperComponent hidePartnershipSection={true}>
                    <MatchMakingAuthForm />
                </WrapperComponent>
            ),
            path: '/login'
        }
    ]);

    const requestorRouter = createHashRouter([
        // {
        //     element: (
        //         <DashboardLayout>
        //             <Home />
        //         </DashboardLayout>
        //     ),
        //     path: '/'
        // },
        {
            element: (
                <DashboardLayout>
                    <Home />
                </DashboardLayout>
            ),
            path: '/'
        },
        {
            element: (
                <DashboardLayout>
                    <ApplicationList />
                </DashboardLayout>
            ),
            path: '/application'
        },
        {
            element: (
                <DashboardLayout>
                    <Profile />
                </DashboardLayout>
            ),
            path: '/profile'
        },
        {
            element: (
                <DashboardLayout>
                    <CreateRequest />
                </DashboardLayout>
            ),
            path: '/create-request'
        },
        {
            element: (
                <DashboardLayout>
                    <CreateRequest />
                </DashboardLayout>
            ),
            path: '/edit-request/:matchMakingId'
        },
        {
            element: (
                <DashboardLayout>
                    <ApplicationDetails />
                </DashboardLayout>
            ),
            path: '/application-detail/:matchMakingId'
        },
        {
            element: (
                <DashboardLayout>
                    <MatchMakingPartnerDetails />
                </DashboardLayout>
            ),
            path: '/application-detail/:matchMakingId/:matchMakingPartnerId'
        },
        {
            element: (
                <DashboardLayout>
                    <ApplicationDetails />
                </DashboardLayout>
            ),
            path: '/meeting-detail/:matchMakingId/:matchMakingPartnerId'
        }
    ]);

    const matchMakingFormRouter = createHashRouter([
        { element: <MatchMakingForm />, path: '/' }
    ]);

    const shortFormRouter = createHashRouter([
        {
            element: <ShortForm />,
            path: '/'
        }
    ]);

    return (
        <AuthProvider>
            <ToastContainerWrapper />
            {(route === '/' || !route) && (
                <RouterProvider router={customerRouter} />
            )}
            {(route === '/dashboard' || !route) && (
                <RouterProvider router={requestorRouter} />
            )}
            {(route === '/registration' || !route) && (
                <RouterProvider router={matchMakingFormRouter} />
            )}
            {(route === '/short-form' || !route) && (
                <RouterProvider router={shortFormRouter} />
            )}
        </AuthProvider>
    );
};

export default App;
